import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';

import * as LancamentosAPI from '../../../../../api/lancamentos';
import * as TabelaAPI from '../../../../../api/tabela';

import { retornaData } from '../../../../../utils/retornaDataFormatada';
import { resumoBaixaAgrupada } from '../helpers/resumoBaixaAgrupada';

export function useBaixaAgrupadaModule({ token, tipo, empresaId, selectedUUIDs, setFlashMessage, handleSubmit }) {
    const [dados, setDados] = useState({
        dataBaixa: retornaData(moment()),
        tipo: tipo || '',
        valorTotalBaixa: 0,
        agrupar: true,
        contaId: '',
        historico: '',
    });

    const [itens, setItens] = useState([]);
    const [optionsConta, setOptionsConta] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // -----------------------------
    // mantém tipo sincronizado
    // -----------------------------
    useEffect(() => {
        setDados((prev) => ({
            ...prev,
            tipo: tipo || '',
        }));
    }, [tipo]);

    // -----------------------------
    // carrega lançamentos selecionados
    // -----------------------------
    useEffect(() => {
        const carregarItens = async () => {
            if (!Array.isArray(selectedUUIDs) || selectedUUIDs.length === 0) {
                setItens([]);
                setDados((prev) => ({
                    ...prev,
                    valorTotalBaixa: 0,
                }));
                return;
            }

            setIsLoading(true);

            try {
                const response = await LancamentosAPI.paraBaixar([selectedUUIDs]);
                const lista = Array.isArray(response?.data) ? response.data : [];

                setItens(lista);

                const resumo = resumoBaixaAgrupada(lista);

                setDados((prev) => ({
                    ...prev,
                    valorTotalBaixa: resumo.total,
                    tipo: tipo || prev.tipo,
                }));

                if (lista.length !== selectedUUIDs.length) {
                    setFlashMessage?.('Alguns lançamentos selecionados não puderam ser carregados.', 'warning');
                }
            } catch (error) {
                console.error(error);
                setItens([]);
                setDados((prev) => ({
                    ...prev,
                    valorTotalBaixa: 0,
                }));

                setFlashMessage?.(
                    error?.response?.data?.message || 'Erro ao carregar lançamentos para baixa.',
                    error?.response?.data?.type || 'error'
                );
            } finally {
                setIsLoading(false);
            }
        };

        carregarItens();
    }, [selectedUUIDs, tipo, setFlashMessage]);

    // -----------------------------
    // carrega contas
    // -----------------------------
    useEffect(() => {
        const carregarContas = async () => {
            if (!empresaId) {
                setOptionsConta([]);
                return;
            }

            try {
                const contas = await TabelaAPI.tabela(empresaId, 'Conta', 'descricao');
                setOptionsConta(Array.isArray(contas) ? contas : []);
            } catch (error) {
                console.error(error);
                setOptionsConta([]);
                setFlashMessage?.('Erro ao carregar contas para baixa agrupada.', 'error');
            }
        };

        carregarContas();
    }, [empresaId, setFlashMessage]);

    // -----------------------------
    // resumo calculado
    // -----------------------------
    const resumo = useMemo(() => {
        return resumoBaixaAgrupada(itens);
    }, [itens]);

    // -----------------------------
    // mantém total sincronizado
    // -----------------------------
    useEffect(() => {
        setDados((prev) => ({
            ...prev,
            valorTotalBaixa: resumo.total,
        }));
    }, [resumo.total]);

    // -----------------------------
    // handlers
    // -----------------------------
    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;

        setDados((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const submit = async (e) => {
        e.preventDefault();

        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            await handleSubmit({
                itens,
                dados,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // -----------------------------
    // trava de submit
    // -----------------------------
    const isSubmitDisabled =
        isSubmitting ||
        isLoading ||
        !dados?.dataBaixa ||
        !dados?.contaId ||
        !resumo?.quantidade ||
        Number(resumo?.total || 0) <= 0;

    return {
        dados,
        setDados,
        itens,
        setItens,
        resumo,
        optionsConta,
        isSubmitting,
        isLoading,
        isSubmitDisabled,
        handleChange,
        submit,
    };
}
